import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import {
  ShieldCheck, Upload, Mail, Phone, User,
  CheckCircle, Loader2, X, FileText, AlertCircle
} from 'lucide-react';

const ClaimForm = ({ businessId, businessName, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', message: '' });
  const [proofDocs, setProofDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [emailState, setEmailState] = useState({
    sending: false, sent: false, code: '', verifying: false, verified: false, error: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Send verification code to claimant's email
  const sendVerification = async () => {
    if (!form.email.trim() || !form.email.includes('@')) {
      setError(t('claimEmailRequired'));
      return;
    }
    setError('');
    setEmailState(prev => ({ ...prev, sending: true, error: '' }));
    try {
      const res = await axiosInstance.post('/claims/send-verification', {
        businessId, email: form.email.trim()
      });
      setEmailState(prev => ({ ...prev, sending: false, sent: true, maskedEmail: res.data.maskedEmail }));
    } catch (err) {
      setEmailState(prev => ({
        ...prev, sending: false,
        error: err.response?.data?.detail || t('claimVerifySendFailed')
      }));
    }
  };

  // Verify the code
  const verifyCode = async () => {
    setEmailState(prev => ({ ...prev, verifying: true, error: '' }));
    try {
      await axiosInstance.post('/claims/verify-code', { businessId, code: emailState.code });
      setEmailState(prev => ({ ...prev, verifying: false, verified: true }));
    } catch (err) {
      setEmailState(prev => ({
        ...prev, verifying: false,
        error: err.response?.data?.detail || t('claimVerifyFailed')
      }));
    }
  };

  // Upload proof doc (optional)
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (proofDocs.length >= 3) { setError(t('claimMaxFiles')); return; }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axiosInstance.post('/claims/upload-proof', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProofDocs(prev => [...prev, { url: res.data.url, name: file.name }]);
    } catch (err) {
      setError(err.response?.data?.detail || t('claimUploadFailed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }, [proofDocs, t]);

  const removeDoc = (i) => setProofDocs(prev => prev.filter((_, idx) => idx !== i));

  // Submit the claim
  const handleSubmit = async () => {
    setError('');
    if (!form.fullName.trim()) { setError(t('claimNameRequired')); return; }
    if (!emailState.verified) { setError(t('claimEmailVerifyRequired')); return; }
    setSubmitting(true);
    try {
      const res = await axiosInstance.post('/claims', {
        businessId,
        fullName: form.fullName,
        email: form.email.trim(),
        phone: form.phone,
        message: form.message,
        proofDocUrls: proofDocs.map(d => d.url),
      });
      onSuccess?.(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('claimError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="claim-form">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <User className="h-3.5 w-3.5 inline mr-1" />{t('claimFullName')} *
        </label>
        <input
          type="text" value={form.fullName}
          onChange={e => updateForm('fullName', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          placeholder={t('claimFullNamePlaceholder')}
          data-testid="claim-fullname-input"
        />
      </div>

      {/* Email + Verification (required) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Mail className="h-3.5 w-3.5 inline mr-1" />{t('claimYourEmail')} *
        </label>
        <div className="flex gap-2">
          <input
            type="email" value={form.email}
            onChange={e => { updateForm('email', e.target.value); if (emailState.verified) setEmailState({ sending: false, sent: false, code: '', verifying: false, verified: false, error: '' }); }}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
            placeholder="your@email.com"
            disabled={emailState.verified}
            data-testid="claim-email-input"
          />
          {!emailState.verified && (
            <button
              onClick={sendVerification}
              disabled={emailState.sending || !form.email.includes('@')}
              className="px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1 whitespace-nowrap"
              data-testid="claim-send-code-btn"
            >
              {emailState.sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {emailState.sent ? t('claimResendCode') : t('claimSendCode')}
            </button>
          )}
          {emailState.verified && (
            <span className="flex items-center gap-1 px-3 py-2 bg-green-50 text-green-700 text-sm rounded-lg font-medium" data-testid="claim-email-verified-badge">
              <CheckCircle className="h-4 w-4" /> {t('claimVerifiedBadge')}
            </span>
          )}
        </div>

        {/* Code input */}
        {emailState.sent && !emailState.verified && (
          <div className="mt-2 flex gap-2 items-center">
            <input
              type="text" maxLength={6} value={emailState.code}
              onChange={e => setEmailState(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '') }))}
              className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center tracking-widest font-mono focus:ring-2 focus:ring-purple-400"
              placeholder="000000"
              data-testid="claim-verify-code-input"
            />
            <button
              onClick={verifyCode}
              disabled={emailState.verifying || emailState.code.length !== 6}
              className="px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1"
              data-testid="claim-verify-code-btn"
            >
              {emailState.verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
              {t('claimVerifyBtn')}
            </button>
            <span className="text-xs text-gray-400">{t('claimCodeSentCheck')}</span>
          </div>
        )}
        {emailState.error && (
          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />{emailState.error}
          </p>
        )}
      </div>

      {/* Phone (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Phone className="h-3.5 w-3.5 inline mr-1" />{t('claimPhone')} <span className="text-gray-400 font-normal">({t('optional')})</span>
        </label>
        <input
          type="tel" value={form.phone}
          onChange={e => updateForm('phone', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          placeholder="+34 ..."
          data-testid="claim-phone-input"
        />
      </div>

      {/* Message (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('claimMessageLabel')} <span className="text-gray-400 font-normal">({t('optional')})</span>
        </label>
        <textarea
          value={form.message} onChange={e => updateForm('message', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 resize-none"
          rows={2} placeholder={t('claimMessagePlaceholder')}
          data-testid="claim-message-input"
        />
      </div>

      {/* Proof docs (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('claimProofTitle')} <span className="text-gray-400 font-normal">({t('optional')})</span>
        </label>
        <p className="text-xs text-gray-400 mb-2">{t('claimProofHint')}</p>
        {proofDocs.map((doc, i) => (
          <div key={i} className="flex items-center justify-between bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200 mb-1.5 text-sm">
            <span className="flex items-center gap-1.5 text-gray-700 truncate"><FileText className="h-3.5 w-3.5 text-purple-500" />{doc.name}</span>
            <button onClick={() => removeDoc(i)} className="text-gray-400 hover:text-red-500 ml-2"><X className="h-4 w-4" /></button>
          </div>
        ))}
        {proofDocs.length < 3 && (
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-xs text-gray-500 hover:border-purple-400 hover:text-purple-600 cursor-pointer transition-colors">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? t('claimUploading') : t('claimUploadFile')}
            <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleFileUpload} disabled={uploading} data-testid="claim-file-input" />
          </label>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2 border-t border-gray-200">
        <button onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200" data-testid="claim-cancel-btn">
          {t('cancel')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || !emailState.verified}
          className="px-5 py-2.5 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-40 flex items-center gap-1.5 transition-colors"
          data-testid="claim-submit-btn"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          {submitting ? t('submittingClaim') : t('claimSubmitFinal')}
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1" data-testid="claim-form-error">
          <AlertCircle className="h-4 w-4" />{error}
        </p>
      )}
    </div>
  );
};

export default ClaimForm;
